import { useEffect } from "react";
import { useIsMobile } from "../hooks/useIsMobile";
import { PageWrapper, DarkNav, DarkFooter } from "../components/PageShell";

const TERMS_CSS = `
  @keyframes termsReveal {
    from { opacity:0; filter:blur(5px); transform:translateY(14px); }
    to   { opacity:1; filter:blur(0); transform:translateY(0); }
  }
  .terms-sec { animation: termsReveal 0.55s both; }
  @media (max-width: 640px) {
    .terms-grid { grid-template-columns: 1fr !important; gap: 0 !important; }
    .terms-num  { display: none !important; }
    .terms-content { padding-bottom: 32px !important; }
  }
  .terms-sec:nth-child(1)  { animation-delay:0.05s; }
  .terms-sec:nth-child(2)  { animation-delay:0.10s; }
  .terms-sec:nth-child(3)  { animation-delay:0.15s; }
  .terms-sec:nth-child(4)  { animation-delay:0.20s; }
  .terms-sec:nth-child(5)  { animation-delay:0.25s; }
  .terms-sec:nth-child(6)  { animation-delay:0.30s; }
  .terms-sec:nth-child(7)  { animation-delay:0.35s; }
  .terms-sec:nth-child(8)  { animation-delay:0.40s; }
  .terms-sec:nth-child(9)  { animation-delay:0.45s; }
  .terms-sec:nth-child(10) { animation-delay:0.50s; }
  .terms-sec:nth-child(11) { animation-delay:0.55s; }
  .terms-sec:nth-child(12) { animation-delay:0.60s; }
  .terms-sec:nth-child(13) { animation-delay:0.65s; }
  .terms-sec:nth-child(14) { animation-delay:0.70s; }
`;


function P({ children }) {
  return <p style={{ fontSize:14.5, color:"rgba(255,255,255,0.6)", lineHeight:1.9, margin:"0 0 14px" }}>{children}</p>;
}
function BL({ items }) {
  return (
    <ul style={{ listStyle:"none", padding:0, margin:"10px 0 16px", display:"flex", flexDirection:"column", gap:10 }}>
      {items.map((it,i) => (
        <li key={i} style={{ display:"flex", gap:12, alignItems:"flex-start" }}>
          <span style={{
            marginTop:8, flexShrink:0, width:18, height:18, borderRadius:5,
            background:"rgba(124,58,237,0.15)", border:"1px solid rgba(124,58,237,0.35)",
            display:"flex", alignItems:"center", justifyContent:"center", fontSize:10, color:"#A78BFA",
          }}>✓</span>
          <span style={{ fontSize:14, color:"rgba(255,255,255,0.62)", lineHeight:1.85 }}>{it}</span>
        </li>
      ))}
    </ul>
  );
}
function IBox({ icon, children }) {
  return (
    <div style={{
      background:"rgba(124,58,237,0.08)", border:"1px solid rgba(124,58,237,0.28)",
      borderLeft:"3px solid #7C3AED", borderRadius:"0 12px 12px 0",
      padding:"14px 18px", margin:"16px 0",
      fontSize:13.5, color:"rgba(196,181,253,0.9)", lineHeight:1.75,
      display:"flex", gap:10, alignItems:"flex-start",
    }}>
      {icon && <span style={{ fontSize:16, flexShrink:0 }}>{icon}</span>}
      <span>{children}</span>
    </div>
  );
}
function CRow({ label, children }) {
  return (
    <div style={{ display:"flex", gap:12, alignItems:"flex-start", fontSize:14, paddingBottom:10, borderBottom:"1px solid rgba(255,255,255,0.05)" }}>
      <span style={{ width:90, flexShrink:0, color:"rgba(167,139,250,0.65)", fontWeight:500, fontSize:12.5 }}>{label}</span>
      {children}
    </div>
  );
}

function Sec({ id, idx, title, children }) {
  return (
    <section id={id} className="terms-sec" style={{ scrollMarginTop:82, marginBottom:0 }}>
      <div className="terms-grid" style={{ display:"grid", gridTemplateColumns:"64px 1fr", gap:24, alignItems:"flex-start" }}>
        {/* Large number */}
        <div className="terms-num" style={{ paddingTop:4 }}>
          <span style={{
            display:"block", fontSize:42, fontWeight:900, lineHeight:1, letterSpacing:-2,
            background:"linear-gradient(135deg,rgba(124,58,237,0.55),rgba(99,102,241,0.2))",
            WebkitBackgroundClip:"text", WebkitTextFillColor:"transparent",
            userSelect:"none",
          }}>
            {String(idx+1).padStart(2,"0")}
          </span>
        </div>
        {/* Content */}
        <div className="terms-content" style={{ paddingBottom:44 }}>
          <h2 style={{ fontSize:20, fontWeight:800, color:"#fff", margin:"0 0 18px", letterSpacing:-0.4 }}>{title}</h2>
          {children}
          {/* Separator */}
          <div style={{ marginTop:32, height:1, background:"linear-gradient(90deg,rgba(124,58,237,0.35),rgba(99,102,241,0.18),transparent)" }} />
        </div>
      </div>
    </section>
  );
}

export default function TermsPage() {
  const isMobile = useIsMobile();
  const px = isMobile ? 16 : 40;

  useEffect(() => {
    document.documentElement.classList.add("page-scrollable");
    return () => document.documentElement.classList.remove("page-scrollable");
  }, []);

  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior:"smooth", block:"start" });

  return (
    <PageWrapper>
      <style>{TERMS_CSS}</style>
      <DarkNav px={px} />

      {/* ── HERO ── */}
      <div style={{ padding:`${isMobile?40:64}px ${px}px ${isMobile?28:48}px`, maxWidth:900, margin:"0 auto" }}>
        <div style={{ display:"flex", alignItems:"center", gap:12, marginBottom:24 }}>
          <div style={{ height:1, flex:1, background:"linear-gradient(90deg,transparent,rgba(124,58,237,0.5))" }} />
          <span style={{ fontSize:10, fontWeight:700, color:"rgba(167,139,250,0.6)", letterSpacing:2, textTransform:"uppercase" }}>Legal Agreement</span>
          <div style={{ height:1, flex:1, background:"linear-gradient(90deg,rgba(124,58,237,0.5),transparent)" }} />
        </div>
        <h1 style={{
          fontSize:`clamp(28px,5vw,56px)`, fontWeight:900, margin:"0 0 16px", letterSpacing:-2, lineHeight:1.05,
          background:"linear-gradient(135deg,#fff 0%,#C4B5FD 60%,#818CF8 100%)",
          WebkitBackgroundClip:"text", WebkitTextFillColor:"transparent",
        }}>Terms &amp; Conditions</h1>
        <p style={{ fontSize:14.5, color:"rgba(255,255,255,0.45)", margin:"0 0 28px", lineHeight:1.75, maxWidth:560 }}>
          Please read these terms carefully before using LorryKhojo. By accessing or using the platform, you agree to be bound by these terms.
        </p>

        {/* Quick-jump chips */}
        {!isMobile && (
          <div style={{ display:"flex", flexWrap:"wrap", gap:8 }}>
            {[
              {id:"parties",label:"Parties"},{id:"service",label:"Service"},{id:"plans",label:"Plans"},
              {id:"payment",label:"Payment"},{id:"refunds",label:"Refunds"},{id:"acceptable",label:"Acceptable Use"},
              {id:"data",label:"Privacy"},{id:"liability",label:"Liability"},{id:"governing",label:"Governing Law"},
            ].map(s => (
              <button key={s.id} onClick={() => scrollTo(s.id)} style={{
                background:"rgba(124,58,237,0.08)", border:"1px solid rgba(124,58,237,0.28)",
                borderRadius:99, padding:"5px 14px", cursor:"pointer",
                fontSize:11.5, fontWeight:600, color:"rgba(196,181,253,0.75)",
                transition:"all 0.15s",
              }}
                onMouseEnter={e => { e.currentTarget.style.background="rgba(124,58,237,0.2)"; e.currentTarget.style.color="#C4B5FD"; }}
                onMouseLeave={e => { e.currentTarget.style.background="rgba(124,58,237,0.08)"; e.currentTarget.style.color="rgba(196,181,253,0.75)"; }}
              >{s.label}</button>
            ))}
          </div>
        )}
      </div>

      {/* ── ARTICLE ── */}
      <div style={{ maxWidth:900, margin:"0 auto", padding:`0 ${px}px ${isMobile?60:100}px` }}>

        <Sec id="parties" idx={0} title="Parties">
          <P>These Terms &amp; Conditions ("Terms") govern your use of <strong>LorryKhojo</strong> ("the Platform" or "Service") — a FASTag-powered fleet intelligence and vehicle tracking platform built and operated by <strong>GVA Labs</strong>, a technology studio operated under <strong>Gupta Vibhor and Associates</strong>, a firm registered in New Delhi, India ("we", "us", "Operator").</P>
          <P>By creating an account or using any feature of the Service, you ("User", "Customer", "you") acknowledge that you have read, understood, and agree to be bound by these Terms. If you do not agree, please do not use the Service.</P>
        </Sec>

        <Sec id="service" idx={1} title="The Service">
          <P>LorryKhojo provides real-time vehicle location intelligence powered by India's <strong>National Electronic Toll Collection (NETC) FASTag network</strong>. The platform surfaces live toll-crossing data, route history, vehicle-level analytics, and fleet management tools through a secure web application.</P>
          <BL items={["Live location updates whenever a vehicle crosses a FASTag-enabled toll plaza.","Last 72 hours of toll-crossing history per vehicle — older crossings are not retained by the NETC network.","Multi-vehicle fleet management with real-time status, tracking toggles, and sharing links.","AI-powered copilot for fleet insights and route analysis.","Shareable live-view links for drivers and logistics partners."]} />
          <IBox icon="ℹ️">LorryKhojo does not use GPS hardware. Location data is derived exclusively from FASTag toll crossings. Between crossings, the platform uses intelligent position estimation. This service is not a substitute for GPS tracking devices.</IBox>
        </Sec>

        <Sec id="accounts" idx={2} title="Accounts">
          <P>You must register with a valid email address and accurate business information. You are solely responsible for maintaining the confidentiality of your login credentials and for all activity that occurs under your account.</P>
          <BL items={["One account per organisation — do not share login credentials across multiple users.","You must ensure that any driver or fleet personnel whose vehicle is tracked has been informed of FASTag data usage as required under the Digital Personal Data Protection Act, 2023.","Notify us immediately at vibhor@lorrykhojo.com if you suspect unauthorised access to your account.","We reserve the right to suspend accounts that provide false registration information."]} />
        </Sec>

        <Sec id="plans" idx={3} title="Free Trial & Plans">
          <IBox icon="🎁">Every new account receives <strong>2 free API calls</strong> upon registration — no payment required. This lets you experience live vehicle tracking before committing to a plan.</IBox>
          <P>After your free calls are used, choose one of the following one-time plans:</P>
          <div style={{ display:"grid", gridTemplateColumns:"repeat(2,1fr)", gap:10, margin:"14px 0" }}>
            {[
              {plan:"Starter",calls:"50 calls",price:"₹300",gst:"₹354 incl. GST"},
              {plan:"Standard",calls:"100 calls",price:"₹550",gst:"₹649 incl. GST"},
              {plan:"Growth",calls:"150 calls",price:"₹750",gst:"₹885 incl. GST"},
              {plan:"Pro",calls:"200 calls",price:"₹900",gst:"₹1,062 incl. GST",highlight:true},
            ].map(p => (
              <div key={p.plan} style={{
                background:p.highlight?"rgba(52,211,153,0.06)":"rgba(124,58,237,0.06)",
                border:`1px solid ${p.highlight?"rgba(52,211,153,0.25)":"rgba(124,58,237,0.2)"}`,
                borderRadius:10, padding:"12px 16px",
              }}>
                <div style={{fontSize:11,fontWeight:800,color:p.highlight?"#34D399":"#A78BFA",marginBottom:4,letterSpacing:0.5}}>{p.plan}{p.highlight?" ★":""}</div>
                <div style={{fontSize:15,fontWeight:800,color:"#fff"}}>{p.calls}</div>
                <div style={{fontSize:13,fontWeight:600,color:"rgba(255,255,255,0.6)",marginTop:4}}>{p.price} base</div>
                <div style={{fontSize:11,color:"rgba(255,255,255,0.28)"}}>{p.gst}</div>
              </div>
            ))}
          </div>
          <BL items={["1 API call = 1 vehicle data refresh from the FASTag/NETC network.","Purchased credits activate instantly after payment verification.","Credits do not expire while your account remains active and in good standing.","Credits are non-transferable between accounts.","Any plan purchase unlocks top-up packs at ₹3.75/call + 18% GST after exhaustion."]} />
        </Sec>

        <Sec id="payment" idx={4} title="Payment">
          <BL items={["All prices are listed in INR and are exclusive of GST. 18% GST is applied at checkout and remitted to the Government of India.","Payments are processed securely through our payment gateway. By completing a transaction, you authorise a one-time charge to your selected payment instrument.","Tax invoices (Invoice series: GVA/LK/XXXX) are generated automatically and emailed to you within minutes of payment confirmation.","Invoices are issued by Gupta Vibhor & Associates. If your organisation requires GSTIN on the invoice for input-tax-credit purposes, contact vibhor@lorrykhojo.com before payment.","API credits are credited to your account instantly upon successful payment verification."]} />
        </Sec>

        <Sec id="refunds" idx={5} title="Cancellation & Refunds">
          <P>You may stop using the Service at any time by ceasing to log in. Because API credits are consumed on use and activated instantly, <strong>any calls already made are non-refundable</strong>. For unused credits:</P>
          <BL items={["Refund requests for unused credits may be considered within 7 days of the original purchase, provided no API calls have been made against that purchase.","Free trial calls (5 calls on registration) are not refundable and carry no cash value.","Top-up packs are non-refundable once any calls from that top-up have been used.","To raise a refund request, email vibhor@lorrykhojo.com with your registered email and payment transaction ID."]} />
        </Sec>

        <Sec id="acceptable" idx={6} title="Acceptable Use">
          <P>LorryKhojo is a business-grade fleet intelligence tool. You agree not to:</P>
          <BL items={["Track any vehicle or individual without the knowledge and lawful authority of the vehicle owner.","Use the Service to surveil or monitor persons for purposes unrelated to legitimate fleet or logistics management.","Attempt to reverse-engineer, scrape, probe, or exploit the FASTag data pipeline, our APIs, or any security mechanism of the platform.","Resell, white-label, or sublicense access to the Service or its data without a prior written agreement with GVA Labs.","Use the platform in any manner that violates Indian law, NETC/NPCI usage guidelines, or the rights of any third party.","Share your account credentials or create multiple accounts to circumvent the per-account API quota."]} />
          <P>Violations may result in immediate account suspension, data removal, and referral to appropriate legal authorities.</P>
        </Sec>

        <Sec id="data" idx={7} title="Data & Privacy">
          <P>LorryKhojo acts as a <strong>Data Processor</strong> in relation to vehicle location data handled through your account. You, as the fleet operator, are the <strong>Data Fiduciary</strong> under the Digital Personal Data Protection Act, 2023 for any personal data (e.g., driver identity, vehicle registration) that can be inferred from your use of the Service.</P>
          <BL items={["FASTag transaction data is sourced from the NETC network via licensed API partners (Masters India) and used solely to power the tracking features you request.","Toll-crossing data is not retained beyond the last 72 hours as sourced from the NETC network — this is a constraint of the upstream data infrastructure, not a LorryKhojo policy.","We do not sell, share, or trade your fleet data or user data with third parties for advertising or commercial profiling.","Vehicle data may be cached briefly on our servers to improve response times. Cached data is cleared on account deletion.","All data in transit is encrypted via TLS. Data at rest is stored on secured cloud infrastructure (Neon PostgreSQL)."]} />
        </Sec>

        <Sec id="availability" idx={8} title="Service Availability">
          <P>We strive for high availability but do not guarantee uninterrupted service. Tracking accuracy and data freshness are fundamentally dependent on:</P>
          <BL items={["The national NETC/FASTag toll network — data is only available when vehicles pass through active toll plazas.","Our upstream API partner (Masters India) — their availability and data freshness directly affect ours.","Standard cloud infrastructure — subject to maintenance windows, typically scheduled off-peak.","Internet connectivity on your device."]} />
          <P>We are not liable for damages caused by toll-network outages, upstream API degradation, force-majeure events, or interruptions beyond our reasonable control.</P>
        </Sec>

        <Sec id="liability" idx={9} title="Limitation of Liability">
          <P>To the maximum extent permitted under Indian law, our aggregate liability for any claim arising from your use of LorryKhojo is limited to the <strong>total fees you paid us in the three (3) months immediately preceding the claim</strong>.</P>
          <P>We are not liable for any indirect, incidental, consequential, or punitive damages — including but not limited to loss of business, loss of revenue, cargo delays, route inefficiencies, or decisions made in reliance on inaccurate or delayed tracking data.</P>
          <IBox icon="⚖️">LorryKhojo location data is derived from FASTag toll crossings and reflects vehicle position at the time of crossing — not a continuous real-time GPS signal. Do not use this Service as the sole basis for time-critical logistics dispatch, legal evidence, or insurance claims.</IBox>
        </Sec>

        <Sec id="termination" idx={10} title="Termination">
          <P>We may suspend or terminate your account for non-payment, material breach of these Terms, fraudulent use, or unlawful conduct. Where practicable, we will provide reasonable advance notice.</P>
          <BL items={["On termination, your vehicle data and fleet history are retained for 30 days to allow data export.","After 30 days, all associated data is permanently deleted in accordance with our data-retention policy.","Unused API credits are forfeited on account termination unless a refund has been approved under the Cancellation & Refunds clause.","You may request account deletion at any time by emailing vibhor@lorrykhojo.com."]} />
        </Sec>

        <Sec id="changes" idx={11} title="Changes to Terms">
          <P>We may revise these Terms to reflect changes in the law, our services, or our business. Material changes will be communicated by email and via an in-app notice at least <strong>14 days</strong> before they take effect. Continued use of the Service after that date constitutes your acceptance of the revised Terms.</P>
        </Sec>

        <Sec id="governing" idx={12} title="Governing Law & Disputes">
          <P>These Terms are governed by and construed in accordance with the <strong>laws of India</strong>. Any dispute, controversy, or claim arising out of or relating to these Terms or the use of the Service shall be subject to the exclusive jurisdiction of the <strong>courts of New Delhi, India</strong>.</P>
          <P>We encourage resolution of disputes through direct correspondence at vibhor@lorrykhojo.com before initiating formal proceedings.</P>
        </Sec>

        <Sec id="contact" idx={13} title="Contact">
          <div style={{ background:"rgba(124,58,237,0.06)", border:"1px solid rgba(124,58,237,0.22)", borderRadius:16, padding:"22px 26px", display:"flex", flexDirection:"column", gap:14 }}>
            <CRow label="Platform"><span style={{color:"rgba(255,255,255,0.82)",fontWeight:600}}>LorryKhojo</span></CRow>
            <CRow label="Built by"><span style={{color:"rgba(255,255,255,0.82)",fontWeight:600}}>GVA Labs (a studio of Gupta Vibhor and Associates)</span></CRow>
            <CRow label="Operator"><span style={{color:"rgba(255,255,255,0.82)",fontWeight:600}}>Gupta Vibhor and Associates, New Delhi, India</span></CRow>
            <CRow label="Support"><a href="mailto:vibhor@lorrykhojo.com" style={{color:"#A78BFA",textDecoration:"none",fontWeight:600,fontSize:14}} onMouseEnter={e=>e.currentTarget.style.color="#fff"} onMouseLeave={e=>e.currentTarget.style.color="#A78BFA"}>vibhor@lorrykhojo.com</a></CRow>
            <CRow label="Billing"><a href="mailto:vibhor@lorrykhojo.com" style={{color:"#A78BFA",textDecoration:"none",fontWeight:600,fontSize:14}} onMouseEnter={e=>e.currentTarget.style.color="#fff"} onMouseLeave={e=>e.currentTarget.style.color="#A78BFA"}>vibhor@lorrykhojo.com</a></CRow>
          </div>
        </Sec>

        <IBox icon="🛡️">Last updated: <strong>June 2026</strong> · LorryKhojo is a product of <strong>GVA Labs</strong>, operated by <strong>Gupta Vibhor and Associates</strong>, New Delhi, India.</IBox>
      </div>

      <DarkFooter px={px} />
    </PageWrapper>
  );
}
